import React, { useEffect, useState } from "react";

export interface Stage {
  name: string;
  status: "complete" | "in-progress" | "not-started" | "cancelled";
  statusText: string;
}

interface ActivityStagesProps {
  stages: Stage[];
  currentStage: string;
}

const ActivityStages: React.FC<ActivityStagesProps> = ({
  stages,
  currentStage,
}) => {
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    // Trigger animation after component mount
    setAnimate(true);
  }, []);

  const getStatusColor = (status: Stage["status"]) => {
    switch (status) {
      case "complete":
        return "bg-green-500";
      case "in-progress":
        return "bg-yellow-500";
      case "cancelled":
        return "bg-red-500";
      case "not-started":
      default:
        return "";
    }
  };

  const getStatusTextColor = (status: Stage["status"]) => {
    switch (status) {
      case "complete":
        return "text-gray-400"; // Lighter shade for completed stages
      case "in-progress":
        return "text-gray-500"; // Slightly darker for in-progress
      case "cancelled":
        return "text-red-600";
      case "not-started":
      default:
        return "text-gray-300"; // Lightest shade for not started
    }
  };

  return (
    <div className="w-full">
      <div className="flex justify-between mb-2">
        {stages.map((stage, index) => (
          <div key={index} className="flex-1 px-1 overflow-hidden">
            <div className="h-2 rounded-full bg-gray-200 overflow-hidden print:bg-gray-300">
              <div
                className={`h-full ${getStatusColor(stage.status)} transition-all duration-500 ease-out print:!w-full print:!transition-none`}
                style={{
                  width:
                    animate && stage.status !== "not-started" ? "100%" : "0%",
                  transitionDelay: `${index * 100}ms`,
                }}
              />
            </div>
          </div>
        ))}
      </div>
      <div className="flex justify-between">
        {stages.map((stage, index) => (
          <div key={index} className="flex-1 px-1 text-left">
            <h3
              className={`text-xs font-semibold transition-all duration-300 ease-out print:!opacity-100 print:!transform-none ${
                stage.name === currentStage ? "text-gray-900" : "text-gray-700"
              }`}
              style={{
                opacity: animate ? 1 : 0,
                transform: animate ? "translateY(0)" : "translateY(10px)",
                transitionDelay: `${index * 100 + 300}ms`,
              }}
            >
              {stage.name}
            </h3>
            {stage.statusText && (
              <p
                className={`text-xs ${getStatusTextColor(stage.status)} mt-[0.10rem] transition-all duration-300 ease-out print:!opacity-100 print:!transform-none`}
                style={{
                  opacity: animate ? 1 : 0,
                  transform: animate ? "translateY(0)" : "translateY(10px)",
                  transitionDelay: `${index * 100 + 400}ms`,
                }}
              >
                {stage.statusText}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ActivityStages;
